"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowLeft, ArrowRight, BookOpen, Search } from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import type { ResourceMetadata } from "@/lib/resources";

interface GuidesListingClientProps {
  resources: ResourceMetadata[];
}

export function GuidesListingClient({ resources }: GuidesListingClientProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const query = searchQuery.toLowerCase();
  const matches = (r: ResourceMetadata) =>
    r.title.toLowerCase().includes(query) || r.summary.toLowerCase().includes(query);

  const pillar = resources.find(r => r.pillar === true);
  const chapters = resources.filter(r => r.pillar !== true);

  const isSearching = searchQuery.length > 0;
  const filteredPillar = isSearching ? (pillar && matches(pillar) ? pillar : null) : pillar;
  const filteredChapters = isSearching ? chapters.filter(matches) : chapters;

  return (
    <div className="relative py-20 md:py-32 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-purple-500/5 blur-[120px] -z-10" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto mb-20">
          <ScrollReveal>
            <Link
              href="/resources"
              className="inline-flex items-center text-base font-medium text-slate-400 hover:text-purple-400 mb-12 transition-colors group"
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Resources
            </Link>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-12">
              <div className="flex items-center gap-6">
                <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 text-purple-400">
                  <BookOpen className="h-8 w-8" />
                </div>
                <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                  Guides
                </h1>
              </div>

              {/* Search Bar */}
              <div className="relative w-full md:w-72 group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 group-focus-within:text-purple-400 transition-colors" />
                <input
                  type="text"
                  placeholder="Search guides..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-slate-900/50 border border-slate-800 rounded-xl py-3 pl-10 pr-4 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-purple-500/50 transition-all"
                />
              </div>
            </div>

            <p className="text-xl md:text-2xl text-slate-400 leading-relaxed max-w-2xl">
              Complete playbooks for AI-driven store management. From daily monitoring to revenue leak detection.
            </p>
          </ScrollReveal>
        </div>

        <div className="max-w-6xl mx-auto">
          {isSearching ? (
            /* Flat search results */
            <div className="grid md:grid-cols-2 gap-10">
              {filteredPillar === null && filteredChapters.length === 0 ? (
                <div className="col-span-full text-center py-32 bg-slate-900/20 rounded-[3rem] border border-slate-800 border-dashed">
                  <p className="text-slate-500 text-lg">No guides match your search.</p>
                </div>
              ) : (
                <>
                  {filteredPillar && (
                    <ScrollReveal>
                      <Link href={`/resources/guides/${filteredPillar.slug}`}>
                        <div className="group p-10 rounded-[2.5rem] bg-slate-900/40 border border-purple-500/30 hover:border-purple-500/60 transition-all h-full flex flex-col relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          <div className="flex items-center justify-between mb-8">
                            <span className="text-xs font-black uppercase tracking-[0.2em] text-purple-400 bg-purple-400/10 px-4 py-1.5 rounded-full border border-purple-400/20">
                              Complete Guide
                            </span>
                          </div>
                          <h3 className="text-3xl font-bold mb-4 group-hover:text-purple-400 transition-colors leading-tight">
                            {filteredPillar.title}
                          </h3>
                          <p className="text-slate-400 mb-10 leading-relaxed text-lg flex-grow">
                            {filteredPillar.summary}
                          </p>
                          <div className="flex items-center text-base font-bold text-purple-400 group-hover:translate-x-2 transition-transform">
                            Read Guide <ArrowRight className="ml-2 h-4 w-4" />
                          </div>
                        </div>
                      </Link>
                    </ScrollReveal>
                  )}
                  {filteredChapters.map((chapter, i) => (
                    <ScrollReveal key={chapter.slug} delay={i * 100}>
                      <Link href={`/resources/guides/${chapter.slug}`}>
                        <div className="group p-10 rounded-[2.5rem] bg-slate-900/40 border border-slate-800/50 hover:border-purple-500/40 transition-all h-full flex flex-col relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          <div className="flex items-center justify-between mb-8">
                            <span className="text-sm font-medium text-slate-500">{chapter.date}</span>
                            <span className="text-xs font-black uppercase tracking-[0.2em] text-purple-400 bg-purple-400/10 px-4 py-1.5 rounded-full border border-purple-400/20">
                              {chapter.category}
                            </span>
                          </div>
                          <h3 className="text-3xl font-bold mb-4 group-hover:text-purple-400 transition-colors leading-tight">
                            {chapter.title}
                          </h3>
                          <p className="text-slate-400 mb-10 leading-relaxed text-lg flex-grow">
                            {chapter.summary}
                          </p>
                          <div className="flex items-center text-base font-bold text-purple-400 group-hover:translate-x-2 transition-transform">
                            Read Chapter <ArrowRight className="ml-2 h-4 w-4" />
                          </div>
                        </div>
                      </Link>
                    </ScrollReveal>
                  ))}
                </>
              )}
            </div>
          ) : (
            /* Default layout: pillar hero + chapter grid */
            <>
              {/* Pillar Hero Card */}
              {filteredPillar && (
                <ScrollReveal>
                  <Link href={`/resources/guides/${filteredPillar.slug}`}>
                    <div className="group p-12 rounded-[3rem] bg-gradient-to-br from-slate-900 to-slate-950 border border-purple-500/30 hover:border-purple-500/60 transition-all relative overflow-hidden mb-16">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 blur-[100px] -z-10 group-hover:bg-purple-500/20 transition-colors" />
                      <div className="relative z-10">
                        <span className="text-xs font-black uppercase tracking-[0.2em] text-purple-400 bg-purple-400/10 px-4 py-1.5 rounded-full border border-purple-400/20 inline-block mb-8">
                          Complete Guide
                        </span>
                        <h2 className="text-4xl md:text-5xl font-bold mb-6 group-hover:text-purple-400 transition-colors leading-tight">
                          {filteredPillar.title}
                        </h2>
                        <p className="text-slate-400 mb-10 leading-relaxed text-xl max-w-3xl">
                          {filteredPillar.summary}
                        </p>
                        <div className="flex items-center text-lg font-bold text-purple-400 group-hover:translate-x-2 transition-transform">
                          Start Reading <ArrowRight className="ml-2 h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              )}

              {/* Chapter Grid */}
              {filteredChapters.length > 0 && (
                <div className="grid md:grid-cols-2 gap-10">
                  {filteredChapters.map((chapter, i) => (
                    <ScrollReveal key={chapter.slug} delay={i * 100}>
                      <Link href={`/resources/guides/${chapter.slug}`}>
                        <div className="group p-10 rounded-[2.5rem] bg-slate-900/40 border border-slate-800/50 hover:border-purple-500/40 transition-all h-full flex flex-col relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          <div className="flex items-center justify-between mb-8">
                            <span className="text-sm font-medium text-slate-500">
                              Chapter {(chapter.order ?? 2) - 1}
                            </span>
                            <span className="text-xs font-black uppercase tracking-[0.2em] text-purple-400 bg-purple-400/10 px-4 py-1.5 rounded-full border border-purple-400/20">
                              {chapter.category}
                            </span>
                          </div>
                          <h3 className="text-3xl font-bold mb-4 group-hover:text-purple-400 transition-colors leading-tight">
                            {chapter.title}
                          </h3>
                          <p className="text-slate-400 mb-10 leading-relaxed text-lg flex-grow">
                            {chapter.summary}
                          </p>
                          <div className="flex items-center text-base font-bold text-purple-400 group-hover:translate-x-2 transition-transform">
                            Read Chapter <ArrowRight className="ml-2 h-4 w-4" />
                          </div>
                        </div>
                      </Link>
                    </ScrollReveal>
                  ))}
                </div>
              )}

              {!filteredPillar && filteredChapters.length === 0 && (
                <div className="text-center py-32 bg-slate-900/20 rounded-[3rem] border border-slate-800 border-dashed">
                  <p className="text-slate-500 text-lg">Guides are currently being finalized. Check back soon.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
